/**
 * Period-over-period comparison table + CSV export for Cached Performance
 * (CR-4751). Rows: metric. Columns: Current | Previous | Delta | 3-point avg.
 */
import { Download } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table'
import { useFormatters } from '../../../contexts/PreferencesContext'
import type {
  DashboardSnapshot, MetricType, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import {
  METRIC_LABELS, asNumber, formatValueByUnit, percentDelta,
  downloadCSV,
} from './helpers'

interface CachedComparisonTableProps {
  latest: DashboardSnapshot[]
  trends: Record<MetricType, TrendPoint[]>
}

const COMPARISON_METRICS: MetricType[] = [
  'open_order_value', 'total_spend', 'monthly_spend',
  'arrears_value', 'arrears_count',
  'otif_percentage', 'otd_percentage',
  'ppm', 'ncr_count', 'ncr_open_count',
  'active_customer_count', 'appeals_count',
]

function deltaColorClass(delta: number): string {
  if (delta === 0) return 'text-muted-foreground'
  return delta > 0 ? 'text-green-600' : 'text-red-600'
}

export function CachedComparisonTable({ latest, trends }: CachedComparisonTableProps) {
  const { formatCurrency, formatDate } = useFormatters()

  const latestByMetric = new Map<MetricType, DashboardSnapshot>()
  latest.forEach((s) => latestByMetric.set(s.metric_type, s))

  const rows = COMPARISON_METRICS.map((metric) => {
    const snap = latestByMetric.get(metric)
    const series = trends[metric] ?? []
    const previous = series.length >= 2 ? series[series.length - 2] : undefined
    const last3 = series.slice(-3)
    const avg3 = last3.length > 0
      ? last3.reduce((sum, p) => sum + asNumber(p.metric_value), 0) / last3.length
      : null
    const delta = snap && previous
      ? percentDelta(asNumber(snap.metric_value), asNumber(previous.metric_value))
      : null
    return { metric, snap, previous, avg3, delta }
  })

  const handleExport = () => {
    const header = ['Metric', 'Current Value', 'Current Date', 'Previous Value', 'Previous Date', '3-Point Avg', 'Delta %']
    const csvRows: string[][] = [header]
    rows.forEach((r) => {
      if (!r.snap) return
      csvRows.push([
        METRIC_LABELS[r.metric],
        r.snap.metric_value,
        r.snap.snapshot_date,
        r.previous?.metric_value ?? '',
        r.previous?.snapshot_date ?? '',
        r.avg3 !== null ? r.avg3.toFixed(2) : '',
        r.delta !== null ? r.delta.toFixed(2) : '',
      ])
    })
    downloadCSV(csvRows, `performance-history-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const hasRows = rows.some((r) => r.snap)

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">Period Comparison</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={!hasRows}
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead>Current</TableHead>
              <TableHead>Previous</TableHead>
              <TableHead>3-point avg</TableHead>
              <TableHead>Delta</TableHead>
              <TableHead>As of</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              if (!r.snap) {
                return (
                  <TableRow key={r.metric}>
                    <TableCell className="font-medium">{METRIC_LABELS[r.metric]}</TableCell>
                    <TableCell colSpan={5} className="text-muted-foreground text-sm">
                      No snapshot captured yet
                    </TableCell>
                  </TableRow>
                )
              }
              const unit = r.snap.metric_unit
              return (
                <TableRow key={r.metric}>
                  <TableCell className="font-medium">{METRIC_LABELS[r.metric]}</TableCell>
                  <TableCell>{formatValueByUnit(r.snap.metric_value, unit, formatCurrency)}</TableCell>
                  <TableCell>
                    {r.previous ? formatValueByUnit(r.previous.metric_value, unit, formatCurrency) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.avg3 !== null ? formatValueByUnit(r.avg3, unit, formatCurrency) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.delta === null ? '—' : (
                      <span className={deltaColorClass(r.delta)}>
                        {r.delta > 0 ? '+' : ''}{r.delta.toFixed(1)}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(r.snap.snapshot_date)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
